import { auth } from '@/lib/api'
import { isTokenExpired } from '@/utils/jwt.utils'
import { useCallback, useEffect, useState } from 'react'
import {
  AuthState,
  LoginCredentials,
  SessionResponse,
  SignInResult,
  SignUpCredentials,
  User
} from './interfaces/auth.interface'

const AUTH_TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'
const REMEMBER_ME_KEY = 'auth_remember_me'

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    loading: true,
    error: null,
    isAuthenticated: false
  })

  // Remove dados do storage (localStorage e sessionStorage)
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(REMEMBER_ME_KEY)
    sessionStorage.removeItem(AUTH_TOKEN_KEY)
    sessionStorage.removeItem(USER_KEY)
  }, [])

  // Carrega dados do storage na inicialização
  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        // Verifica se deve usar localStorage ou sessionStorage
        const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true'
        const storage = rememberMe ? localStorage : sessionStorage

        const token = storage.getItem(AUTH_TOKEN_KEY)
        const userStr = storage.getItem(USER_KEY)

        if (token && userStr) {
          // Verifica se o token está expirado
          if (isTokenExpired(token)) {
            // Token expirado, limpa dados
            clearAuthData()
            setState(prev => ({ ...prev, loading: false, isAuthenticated: false }))
            return
          }
          
          const user = JSON.parse(userStr)
          setState(prev => ({
            ...prev,
            user,
            token,
            isAuthenticated: true,
            loading: false
          }))
        } else {
          setState(prev => ({ ...prev, loading: false }))
        }
      } catch (error) {
        console.error('Erro ao carregar autenticação do storage:', error)
        setState(prev => ({ ...prev, loading: false }))
      }
    }

    loadStoredAuth()
  }, [clearAuthData])

  // Nota: sessionStorage é automaticamente limpo quando o navegador/aba fecha
  // Não é necessário adicionar listener para limpar manualmente

  // Salva dados no storage (localStorage ou sessionStorage baseado em rememberMe)
  const saveAuthData = useCallback((user: User, token: string, rememberMe: boolean = true) => {
    // Salva a preferência rememberMe
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString())
    
    // Escolhe o storage baseado em rememberMe
    const storage = rememberMe ? localStorage : sessionStorage
    
    // Limpa dados do outro storage para evitar conflitos
    if (rememberMe) {
      sessionStorage.removeItem(AUTH_TOKEN_KEY)
      sessionStorage.removeItem(USER_KEY)
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
    }
    
    // Salva dados no storage escolhido
    storage.setItem(AUTH_TOKEN_KEY, token)
    storage.setItem(USER_KEY, JSON.stringify(user))
  }, [])

  // Atualiza estado
  const updateState = useCallback((updates: Partial<AuthState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  // Login
  const signIn = useCallback(
    async (credentials: LoginCredentials): Promise<SignInResult> => {
      try {
        updateState({ loading: true, error: null })

        const response: any = await auth.signIn(credentials)

        if (response.user && response.token) {
          const rememberMe = credentials.rememberMe ?? true
          saveAuthData(response.user, response.token, rememberMe)
          updateState({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null
          })
          
          // Retorna empresas para processamento no Login.tsx
          return {
            user: response.user,
            token: response.token,
            empresas: response.empresas || []
          }
        } else {
          throw new Error('Resposta de login inválida')
        }
      } catch (error: any) {
        const errorMessage = error?.message || 'Erro no login'
        updateState({
          loading: false,
          error: errorMessage,
          isAuthenticated: false
        })
        throw error
      }
    },
    [saveAuthData, updateState]
  )

  // Registro
  const signUp = useCallback(
    async (credentials: SignUpCredentials) => {
      try {
        updateState({ loading: true, error: null })

        const response = await auth.signUp(credentials)

        if (response.user && response.token) {
          const rememberMe = credentials.rememberMe ?? true
          saveAuthData(response.user, response.token, rememberMe)
          updateState({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null
          })
        } else {
          throw new Error('Resposta de registro inválida')
        }
      } catch (error: any) {
        const errorMessage = error?.message || 'Erro no registro'
        updateState({
          loading: false,
          error: errorMessage,
          isAuthenticated: false
        })
        throw error
      }
    },
    [saveAuthData, updateState]
  )

  // Logout
  const signOut = useCallback(async () => {
    try {
      updateState({ loading: true })

      // Chama API de logout
      await auth.signOut()
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      // Limpa dados locais independente do resultado da API
      clearAuthData()
      updateState({
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      })
    }
  }, [clearAuthData, updateState])

  // Atualizar sessão
  const refreshSession = useCallback(async () => {
    try {
      updateState({ loading: true, error: null })

      const response: SessionResponse = await auth.session()

      if (response.user) {
        // Obtém token do storage correto baseado em rememberMe
        const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true'
        const storage = rememberMe ? localStorage : sessionStorage
        const currentToken = storage.getItem(AUTH_TOKEN_KEY)
        
        if (currentToken) {
          saveAuthData(response.user, currentToken, rememberMe)
        }

        updateState({
          user: response.user,
          isAuthenticated: true,
          loading: false,
          error: null
        })
      } else {
        throw new Error('Sessão inválida')
      }
    } catch (error: any) {
      console.error('Erro ao atualizar sessão:', error)

      // Se erro 401, limpa autenticação
      if (
        error?.message?.includes('401') ||
        error?.message?.includes('Token')
      ) {
        clearAuthData()
        updateState({
          user: null,
          token: null,
          isAuthenticated: false,
          loading: false,
          error: 'Sessão expirada'
        })
      } else {
        updateState({
          loading: false,
          error: error?.message || 'Erro ao atualizar sessão'
        })
      }
    }
  }, [saveAuthData, clearAuthData, updateState])

  // Limpar erro
  const clearError = useCallback(() => {
    updateState({ error: null })
  }, [updateState])

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    refreshSession,
    clearError
  }
}
